import React, { useEffect, useRef } from 'react';
import { FaTimes } from 'react-icons/fa';
import './LegalModal.css';

const PrivacyPolicyModal = ({ isOpen, onClose }) => {
  const modalRef = useRef(null);

  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
      modalRef.current?.focus();
    } else {
      document.body.style.overflow = 'unset';
    }

    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [isOpen]);

  useEffect(() => {
    const handleEscape = (e) => {
      if (e.key === 'Escape') onClose();
    };

    if (isOpen) {
      document.addEventListener('keydown', handleEscape);
    }

    return () => document.removeEventListener('keydown', handleEscape);
  }, [isOpen, onClose]);

  const handleBackdropClick = (e) => {
    if (e.target === e.currentTarget) onClose();
  };

  if (!isOpen) return null;

  return (
    <div className="legal-modal-overlay" onClick={handleBackdropClick}>
      <div
        className="legal-modal-container"
        ref={modalRef}
        tabIndex={-1}
        role="dialog"
        aria-modal="true"
        aria-labelledby="privacy-modal-title"
      >
        {/* Modal Header */}
        <div className="legal-modal-header">
          <h2 id="privacy-modal-title" className="legal-modal-title">
            Privacy Policy
          </h2>
          <button
            className="legal-modal-close"
            onClick={onClose}
            aria-label="Close privacy policy"
          >
            <FaTimes />
          </button>
        </div>

        {/* Modal Body */}
        <div className="legal-modal-body">
          <div className="legal-content">
            <p className="last-updated">Last updated July 23, 2025</p>
            
            <div className="legal-section">
              <p className="legal-paragraph">
                This Privacy Notice for Mealzy Foods ("we," "us," or "our"), describes how and why we might access,
                collect, store, use, and/or share ("process") your personal information when you use our services
                ("Services"), including when you:
              </p>
              <p className="legal-bullet">
                • Download and use our mobile application (Mealzy Foods), or any other application of ours that links to this Privacy Notice
              </p>
              <p className="legal-bullet">
                • Engage with us in other related ways, including any sales, marketing, or events
              </p>
              <p className="legal-paragraph">
                Questions or concerns? Reading this Privacy Notice will help you understand your privacy rights and choices.
                If you do not agree with our policies and practices, please do not use our Services.
              </p>
            </div>

            <div className="legal-section">
              <h3 className="legal-section-title">SUMMARY OF KEY POINTS</h3>
              <p className="legal-paragraph">
                This summary provides key points from our Privacy Notice, but you can find out more details about any of these
                topics by reading the full notice below.
              </p>
            </div>

            <div className="legal-section">
              <h3 className="legal-section-title">What personal information do we process?</h3>
              <p className="legal-paragraph">
                When you visit, use, or navigate our Services, we may process personal information such as your name, email address, phone number, and delivery address.
              </p>
            </div>

            <div className="legal-section">
              <h3 className="legal-section-title">Do we process any sensitive personal information?</h3>
              <p className="legal-paragraph">
                We do not process sensitive personal information.
              </p>
            </div>

            <div className="legal-section">
              <h3 className="legal-section-title">Do we collect any information from third parties?</h3>
              <p className="legal-paragraph">
                We do not collect any information from third parties.
              </p>
            </div>

            <div className="legal-section">
              <h3 className="legal-section-title">How do we process your information?</h3>
              <p className="legal-paragraph">
                We process your information to provide, improve, and administer our Services, communicate with you, for security and fraud prevention, and to comply with law.
              </p>
            </div>

            <div className="legal-section">
              <h3 className="legal-section-title">In what situations and with which parties do we share personal information?</h3>
              <p className="legal-paragraph">
                We may share information in specific situations and with specific third parties, primarily for processing payments and deliveries.
              </p>
            </div>

            <div className="legal-section">
              <h3 className="legal-section-title">How do we keep your information safe?</h3>
              <p className="legal-paragraph">
                We have organizational and technical processes and procedures in place to protect your personal information. However, no electronic transmission over the internet or information storage technology can be guaranteed to be 100% secure.
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PrivacyPolicyModal;
